import { useMemo, useCallback } from 'react';
import {
  useInfiniteQuery,
  InfiniteData,
  useQuery,
  GetNextPageParamFunction,
  useMutation,
} from 'react-query';
import produce from 'immer';
import { FEED_NEW_COUNT_URL, NEW_RECOMMEND_FEED_INTERVAL } from './count';
import ReactGA4 from 'react-ga4';
import { isEmpty, omit } from 'lodash';
import { useHistory, useLocation } from 'react-router-dom';
import dayjs from 'dayjs';

import { request, queryClient, useCurrentUserId, toastError } from '@/utils';
import {
  setChannelStreamListQuery,
  setChannelStreamQuoteListQuery,
} from '@/views/Channel/hooks/query';

import { FeedItem, FeedArticleDetail, FeedRepostDetail, FeedArticle } from '../types';
import {
  setQueryFeedAllUsersInListByFeedList,
  setQueryFeedAllUsersInListByTopOfficials,
  setQueryFeedAllUsersInListByArticle,
  setQueryFeedAllUsersInListByTopComments,
  setQueryFeedAllUsersInListByTopContributors,
  setStreamTopUsersInList,
  setRecommendUserInList,
  setChannelUserInList,
  set24hTopXPEarnedUsersInList,
} from './setQuery';
import { transArticle, transComment, transAuthor, isHashTag, getShowVip } from '../utils';
import { Filters } from '../components/StreamFilter/hooks';

export const FEEDS_PAGE_COUNT = 50;

export const LIST_URL: Record<
  string,
  {
    url: string;
    resKey: string;
    idKey?: string;
    paginateKey?: string;
    paginatePicker?: string;
    pageCount?: number;
    // FIXME: 暂时打个 patch，解决第二页开始有 pinned post 乱序
    isPin0?: boolean;
  }
> = {
  feeds: {
    url: '/feed/list',
    resKey: 'feeds',
    idKey: 'user_id',
  },
  feed_search: {
    url: '/feed/search',
    resKey: 'feeds',
    idKey: 'user_id',
  },
  hot_feeds: {
    url: '/feed/hot_list',
    resKey: 'feeds',
    idKey: 'user_id',
  },
  post_list: {
    url: '/article/post_list',
    resKey: 'post_list',
    idKey: 'user_id',
    isPin0: true,
  },
  repost_list: {
    url: '/article/repost_list',
    resKey: 'repost_list',
    idKey: 'user_id',
  },
  official_post: {
    url: '/official/post_articles',
    resKey: 'post_list',
    idKey: 'id',
    isPin0: true,
  },
  official_repost: {
    url: '/official/repost_articles',
    resKey: 'repost_list',
    idKey: 'id',
  },
  official_proposal: {
    url: '/official/proposals',
    resKey: 'proposals',
    idKey: 'id',
  },
  official_hot_proposal: {
    url: '/official/proposals_hot',
    resKey: 'proposals',
    idKey: 'id',
  },
  group_pin_list: {
    url: '/group/pin_list',
    resKey: 'pin_list',
    idKey: 'id',
  },
  group_repost_list: {
    url: '/group/repost_list',
    resKey: 'repost_list',
    idKey: 'id',
  },
  unanswered_list: {
    url: '/article/pay_qa/unanswered_list',
    resKey: 'post_list',
    idKey: 'user_id',
  },
  answered_list: {
    url: '/article/pay_qa/answered_list',
    resKey: 'post_list',
    idKey: 'user_id',
    paginateKey: 'update_at',
    paginatePicker: 'pay_qa.update_at',
  },
  bookmarks: {
    url: '/bookmark/list',
    resKey: 'bookmarks',
    idKey: 'user_id',
    paginateKey: 'bookmarked_at',
  },
  recommend_feeds: {
    url: '/feed/recommend_list',
    resKey: 'feeds',
    idKey: 'user_id',
    pageCount: 20,
  },
  quest_list: {
    url: '/quest/list',
    resKey: 'quests',
  },
  official_quest_list: {
    url: '/official/quests',
    resKey: 'quests',
    idKey: 'id',
  },
  article_quote_list: {
    url: '/article/quote_list',
    resKey: 'quote_list',
    idKey: 'id',
  },
};

type QueryOption = {
  getNextPageParam?: GetNextPageParamFunction;
  cacheTime?: number;
  staleTime?: number;
};

const buildFeedListHook =
  <R extends FeedArticleDetail, T extends keyof typeof LIST_URL>(
    type: T,
    queryOption?: QueryOption,
  ) =>
  ({
    address,
    enabled = true,
    requestParams,
  }: {
    address?: string;
    enabled?: boolean;
    requestParams?: Record<string, string | number>;
  }) => {
    const currentId = useCurrentUserId();
    const { url, resKey, idKey, paginateKey, paginatePicker, pageCount, isPin0 } = LIST_URL[type];

    const _pageCount = pageCount || FEEDS_PAGE_COUNT;

    const select = useCallback(
      (data: InfiniteData<Record<T, R[]> & { total_count?: number }>) => {
        return produce(data, draft => {
          draft.pages.forEach(page =>
            (page as Record<T, R[]>)[resKey as T]?.forEach(feed => {
              transArticle(feed.article);
              feed.comments?.forEach(x => transComment(x));

              feed.article._contributorId = feed.article.creator?.is_following
                ? feed.article.creator_id
                : (feed as unknown as FeedItem).repost_list?.[0]?.user_id;

              (feed as unknown as FeedItem).repost_list?.forEach(x => {
                x._author = transAuthor({ user: x.user });
              });
            }),
          );
        });
      },
      [resKey],
    );

    // 需要加上目标地址，不然 cache router 会有问题
    const queryKey = useMemo(
      () => [url, currentId, address, requestParams],
      [url, currentId, address, requestParams],
    );

    const {
      data,
      isLoading,
      fetchNextPage,
      isFetchingNextPage,
      hasNextPage,
      isFetchingPreviousPage,
      fetchPreviousPage,
      refetch,
      isFetching,
    } = useInfiniteQuery(
      // 没有使用 address 做为 key，考虑到后面的 setQueryData 可能不知道这个 address；
      // 而且同一时间，也不应该出现相同 type/url，不同 address 的 list；
      // 用 url 和 currentId 就足够了
      queryKey,
      async ({ pageParam }) => {
        const params: any = {
          limit: _pageCount,
        };

        if (requestParams) {
          Object.assign(params, requestParams);
        }

        if (pageParam) {
          Object.assign(params, pageParam);
        }

        if (address && idKey) {
          params[idKey] = address;
        }

        return request.get<
          Record<T, R[]> & {
            total_count?: number;
          }
        >(url, params);
      },
      {
        keepPreviousData: false,
        getNextPageParam: (lastPage, pages) => {
          const length = lastPage[resKey as T]?.length;
          if (length === _pageCount) {
            const lastone = lastPage[resKey as T]![length - 1];

            if (!paginateKey) {
              const nextParams: Record<string, number | string> = {
                create_at: (lastone as any)?.create_at || lastone?.article?.create_at,
              };

              if (isPin0 && pages.length) {
                nextParams.is_pinned = 0;
              }

              return nextParams;
            }

            let v: any = lastone?.article?.[paginateKey as keyof FeedArticle];

            if (paginatePicker) {
              v = paginatePicker.split('.').reduce((m, n) => {
                return (m as any)?.[n];
              }, lastone?.article);
            }

            return {
              [paginateKey]: v || '',
            };
          }
        },
        enabled: (!idKey || !!address) && enabled,
        select,
        onError(err: any) {
          if (err?.name !== 'AbortError') {
            toastError(err);
          }
        },
        ...queryOption,
      },
    );

    const result = useMemo(() => data?.pages?.flatMap(x => x[resKey as T]), [data, resKey]);

    const refetchFirstPage = useCallback(
      (newCountUrl?: string) => {
        if (queryClient.getQueryData(queryKey)) {
          queryClient.setQueryData<InfiniteData<Record<string, FeedArticleDetail[]>> | undefined>(
            queryKey,
            pre => {
              if (!pre) {
                return pre;
              }
              if (queryClient.getQueryData([newCountUrl || FEED_NEW_COUNT_URL, currentId])) {
                queryClient.setQueryData([newCountUrl || FEED_NEW_COUNT_URL, currentId], () => {
                  return {
                    has_new: false,
                    count: 0,
                  };
                });
              }
              return produce(pre, draft => {
                draft.pages = draft.pages.slice(0, 1);
                draft.pageParams = draft.pageParams.slice(0, 1);
              });
            },
          );
        }
        refetch();
      },
      [queryKey, refetch, currentId],
    );

    return {
      result,
      isLoading,
      fetchNextPage,
      isFetchingNextPage,
      hasNextPage,
      refetch,
      refetchFirstPage,
      isFetching,
      isFetchingPreviousPage,
      fetchPreviousPage,
      totalCount: data?.pages?.[0]?.total_count,
    };
  };

export const invalidStreamPostList = (userId: string) => {
  queryClient.invalidateQueries(['/article/post_list', userId]);
};

export const invalidStreamList = (userId: string) => {
  Object.values(LIST_URL).forEach(({ url }) => {
    queryClient.invalidateQueries([url, userId]);
  });
};

export const useFeedList = buildFeedListHook<FeedItem, 'feeds'>('feeds');
export const useArticlePostList = buildFeedListHook<FeedArticleDetail, 'post_list'>('post_list');
export const useArticleRepostList = buildFeedListHook<FeedRepostDetail, 'repost_list'>(
  'repost_list',
);

export const useOfficialPostList = buildFeedListHook<FeedArticleDetail, 'official_post'>(
  'official_post',
);
export const useOfficialRepostList = buildFeedListHook<FeedRepostDetail, 'official_repost'>(
  'official_repost',
);
export const useGroupSharedPostList = buildFeedListHook<FeedRepostDetail, 'group_repost_list'>(
  'group_repost_list',
);
export const useGroupPinnedPostList = buildFeedListHook<FeedRepostDetail, 'group_pin_list'>(
  'group_pin_list',
);

export const useUnansweredQaList = buildFeedListHook<FeedRepostDetail, 'unanswered_list'>(
  'unanswered_list',
);

export const useAnsweredQaList = buildFeedListHook<FeedRepostDetail, 'answered_list'>(
  'answered_list',
);

export const useBookmarkList = buildFeedListHook<FeedRepostDetail, 'bookmarks'>('bookmarks');

export const useQuestList = buildFeedListHook<FeedArticleDetail, 'quest_list'>('quest_list');

export const useOfficialQuestList = buildFeedListHook<FeedArticleDetail, 'official_quest_list'>(
  'official_quest_list',
);

export const useArticleQuoteList = buildFeedListHook<FeedArticleDetail, 'article_quote_list'>(
  'article_quote_list',
);

export const useRecommendFeedList = buildFeedListHook<FeedItem, 'recommend_feeds'>(
  'recommend_feeds',
  {
    getNextPageParam: lastPage => {
      // @ts-ignore
      const length = lastPage?.feeds?.length;
      if (!length) {
        return false;
      }
      // 后端每次都会返回新的内容，直到返回为空的时候判断结束
      return true;
    },
    cacheTime: NEW_RECOMMEND_FEED_INTERVAL,
    staleTime: NEW_RECOMMEND_FEED_INTERVAL,
  },
);

// 现在总共也就 100 条，也就是说现在不用翻页
const HOT_PAGE_LIMIT = 100;

const SEARCH_PAGE_LIMIT = 20;

export const useHotFeedList = (limit = HOT_PAGE_LIMIT) => {
  const currentId = useCurrentUserId();

  const select = useCallback((data: InfiniteData<Record<'feeds', FeedItem[]>>) => {
    return produce(data, draft => {
      draft.pages.forEach(page =>
        page.feeds?.forEach(feed => {
          if (feed?.prime_post_issue) {
            // list接口中，后端没有在article中返回prime_post_issue，返回在feed里
            feed.article.prime_post_issue = feed.prime_post_issue;
          }
          transArticle(feed.article);
          feed.comments?.forEach(x => transComment(x));

          // 不设置 contributor_id
          (feed as unknown as FeedItem).repost_list?.forEach(x => {
            x._author = transAuthor({ user: x.user });
            x._showVip = getShowVip(x.user);
          });
        }),
      );
    });
  }, []);

  const queryKey = useMemo(() => [LIST_URL.hot_feeds.url, currentId], [currentId]);

  const {
    data,
    isLoading,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    isFetchingPreviousPage,
    fetchPreviousPage,
    refetch,
    isFetching,
  } = useInfiniteQuery(
    // 没有使用 address 做为 key，考虑到后面的 setQueryData 可能不知道这个 address；
    // 而且同一时间，也不应该出现相同 type/url，不同 address 的 list；
    // 用 url 和 currentId 就足够了
    queryKey,
    async ({ pageParam }) => {
      const params: any = {
        limit,
      };

      if (pageParam) {
        Object.assign(params, pageParam);
      }

      return request.get<Record<'feeds', FeedItem[]>>(LIST_URL.hot_feeds.url, params);
    },
    {
      keepPreviousData: false,
      getNextPageParam: (lastPage, pages) => {
        const length = lastPage.feeds?.length;
        if (length >= limit) {
          return {
            start: pages.length * limit,
          };
        }
        // Return false means no next page
        return false;
      },
      select,
      onError(err: any) {
        toastError(err);
      },
    },
  );

  const result = useMemo(
    () => data?.pages?.flatMap(x => x.feeds?.filter(x => !x.article.is_withdrawn)),
    [data],
  );

  const refetchFirstPage = useCallback(() => {
    if (queryClient.getQueryData(queryKey)) {
      queryClient.setQueryData<InfiniteData<Record<string, FeedArticleDetail[]>> | undefined>(
        queryKey,
        pre => {
          if (!pre) {
            return pre;
          }
          return produce(pre, draft => {
            draft.pages = draft.pages.slice(0, 1);
            draft.pageParams = draft.pageParams.slice(0, 1);
          });
        },
      );
    }
    refetch();
  }, [queryKey, refetch]);

  return {
    result,
    isLoading,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    refetch,
    isFetching,
    isFetchingPreviousPage,
    fetchPreviousPage,
    refetchFirstPage,
  };
};
export const useFeedSearchList = ({
  q,
  sortBy,
  filters,
  enabled,
  issueId,
}: {
  q: string;
  sortBy?: '-create_at' | '-hot_value' | '-read_count';
  filters: Filters;
  enabled: boolean;
  issueId?: string;
}) => {
  const currentId = useCurrentUserId();
  const location = useLocation<{ gaAction?: string }>();
  const history = useHistory();
  const select = useCallback((data: InfiniteData<Record<'feeds', FeedItem[]>>) => {
    return produce(data, draft => {
      draft.pages.forEach(page =>
        page.feeds?.forEach(feed => {
          if (feed?.prime_post_issue) {
            // list接口中，后端没有在article中返回prime_post_issue，返回在feed里
            feed.article.prime_post_issue = feed.prime_post_issue;
          }
          transArticle(feed.article);
          feed.comments?.forEach(x => transComment(x));

          // 不设置 contributor_id
          (feed as unknown as FeedItem).repost_list?.forEach(x => {
            x._author = transAuthor({ user: x.user });
            x._showVip = getShowVip(x.user);
          });
        }),
      );
    });
  }, []);

  const queryKey = useMemo(
    () => [LIST_URL.feed_search.url, currentId, q, sortBy, filters, issueId],
    [currentId, filters, issueId, q, sortBy],
  );

  const {
    data,
    isLoading,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    isFetchingPreviousPage,
    fetchPreviousPage,
    refetch,
    isFetching,
  } = useInfiniteQuery(
    // 没有使用 address 做为 key，考虑到后面的 setQueryData 可能不知道这个 address；
    // 而且同一时间，也不应该出现相同 type/url，不同 address 的 list；
    // 用 url 和 currentId 就足够了
    queryKey,
    async ({ pageParam }) => {
      const params: any = {
        q,
        start: 0,
        limit: SEARCH_PAGE_LIMIT,
        order_by: sortBy ? sortBy : undefined,
        types: !isEmpty(filters?.types) ? filters?.types : undefined,
        creator_usd_value: filters?.creator_usd_value
          ? Number(filters?.creator_usd_value)
          : undefined,
        create_at: filters?.create_at
          ? dayjs().subtract(Number(filters.create_at), 'day').unix()
          : undefined,
        issue_id: issueId ? issueId : undefined,
      };

      if (pageParam) {
        Object.assign(params, pageParam);
      }

      return request.get<Record<'feeds', FeedItem[]>>(LIST_URL.feed_search.url, params);
    },
    {
      keepPreviousData: false,
      getNextPageParam: (lastPage, pages) => {
        const length = lastPage.feeds?.length;
        if (length >= SEARCH_PAGE_LIMIT) {
          return {
            start: pages.length * SEARCH_PAGE_LIMIT,
          };
        }
        // Return false means no next page
        return false;
      },
      select,
      enabled,
      onSuccess(res) {
        if (res?.pageParams?.length <= 1) {
          const tagStr = q?.slice(1)?.toLowerCase();
          if (tagStr && isHashTag(q)) {
            const actionName = location.state?.gaAction || 'Search_Tag_Input';
            ReactGA4.event({
              category: 'Search Stream Tag',
              action: actionName,
            });
            // v2
            ReactGA4.event({
              category: 'Search Stream Tag V2',
              action: `${actionName}_V2_${tagStr}`,
            });
            if (location.state?.gaAction) {
              history.replace({ ...location, state: omit(location.state, 'gaAction') });
            }
          }
        }
      },
      onError(err: any) {
        toastError(err);
      },
    },
  );

  const result = useMemo(
    () => data?.pages?.flatMap(x => x.feeds?.filter(x => !x.article.is_withdrawn)),
    [data],
  );

  return {
    result,
    isLoading,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    refetch,
    isFetching,
    isFetchingPreviousPage,
    fetchPreviousPage,
  };
};

export const setQueryFeedDetailInList = (
  currentAddress: string,
  id: number,
  recipe: (article: FeedArticleDetail) => void,
  excludeUrl?: string,
) => {
  // 理论上同一时间，只会存在一个 list 的 cache
  Object.values(LIST_URL).forEach(({ url, resKey }) => {
    if (url === excludeUrl) {
      return;
    }
    queryClient.setQueriesData<InfiniteData<Record<string, FeedArticleDetail[]>> | undefined>(
      [url, currentAddress],
      pre => {
        // 不存在这个列表的时候，直接返回了
        if (!pre) {
          return pre;
        }

        return produce(pre, draft => {
          // 使用 find 提前退出循环
          draft.pages.find(x => {
            const record = x[resKey]?.find((p: FeedArticleDetail) => p.article.id === id);

            if (record) {
              recipe(record);

              return true;
            }

            return false;
          });

          return draft;
        });
      },
    );
  });

  setChannelStreamListQuery(id, currentAddress!, recipe);
};

export const setQuoteFeedDetailInList = (
  currentAddress: string,
  id: number,
  recipe: (article: FeedArticleDetail) => void,
  excludeUrl?: string,
) => {
  // 理论上同一时间，只会存在一个 list 的 cache
  Object.values(LIST_URL).forEach(({ url, resKey }) => {
    if (url === excludeUrl) {
      return;
    }
    queryClient.setQueriesData<InfiniteData<Record<string, FeedArticleDetail[]>> | undefined>(
      [url, currentAddress],
      pre => {
        // 不存在这个列表的时候，直接返回了
        if (!pre) {
          return pre;
        }

        return produce(pre, draft => {
          draft.pages.forEach(x => {
            x[resKey]
              ?.filter((p: FeedArticleDetail) => p.article.quote_article?.id === id)
              .forEach(record => {
                recipe({ article: record.article.quote_article! });
              });
          });

          return draft;
        });
      },
    );
  });

  setChannelStreamQuoteListQuery(id, currentAddress!, recipe);
};

export const setQueryFeedAllUsersInList = (
  currentAddress: string,
  userId: string,
  userFollowHandler: (user?: UnionAccount) => void,
) => {
  const updateQueryUsersParams = {
    currentAddress,
    userId,
    userFollowHandler,
  };
  setQueryFeedAllUsersInListByFeedList(updateQueryUsersParams);
  setQueryFeedAllUsersInListByTopContributors(updateQueryUsersParams);
  setQueryFeedAllUsersInListByTopOfficials(updateQueryUsersParams);
  setQueryFeedAllUsersInListByArticle(updateQueryUsersParams);
  setQueryFeedAllUsersInListByTopComments(updateQueryUsersParams);
  setStreamTopUsersInList(updateQueryUsersParams);
  setRecommendUserInList(updateQueryUsersParams);
  setChannelUserInList(updateQueryUsersParams);
  set24hTopXPEarnedUsersInList(updateQueryUsersParams);
};

export const useLatestStream = (address: string) => {
  return useQuery(['latest-stream', address], () => {
    return request.get<{ article: FeedArticle }>('/article/recent_post', {
      user_id: address,
    });
  });
};

export const useOfficialLatestStream = (id: string) => {
  return useQuery(['official-latest-stream', id], () => {
    return request.get<{ article: FeedArticle }>('/official/recent_article', {
      id,
    });
  });
};

export const useHidePost = (articleId: number) => {
  const currentAddress = useCurrentUserId();
  return useMutation(
    () => request.post<{ is_success: boolean }>('/user/hide_post', { article_id: articleId }),
    {
      onSuccess: res => {
        if (res?.is_success) {
          const handle = (pre?: FeedArticleDetail) => {
            if (pre?.article) {
              pre.article._isHidden = true;
            }
          };
          setQueryFeedDetailInList(currentAddress!, articleId, handle);
        }
      },
      onError: error => toastError(error),
    },
  );
};

export const useUnHidePost = (articleId: number) => {
  const currentAddress = useCurrentUserId();
  return useMutation(
    () => request.post<{ is_success: boolean }>('/user/unhide_post', { article_id: articleId }),
    {
      onSuccess: res => {
        if (res?.is_success) {
          const handle = (pre?: FeedArticleDetail) => {
            if (pre?.article) {
              pre.article._isHidden = false;
            }
          };
          setQueryFeedDetailInList(currentAddress!, articleId, handle);
        }
      },
      onError: error => toastError(error),
    },
  );
};
